import React from "react";
import { useFormContext } from "react-hook-form";

import FormDetailsEditor from "./editors/form-details-editor";
import JobDetailsEditor from "./editors/job-details-editor";
import ProctoringSettingsEditor from "./editors/proctoring-settings-editor";
import QualifyingQuestionsEditor from "./editors/qualifying-questions-editor";
import QuizSettingsEditor from "./editors/quiz-settings-editor";
import ReferralRewardsEditor from "./editors/referral-rewards-editor";
import VideoResumeEditor from "./editors/video-resume-editor";
import { FormData } from "./types";

interface SectionEditorProps {
  section: string;
  onBack: () => void;
  onNext?: () => void;
}

const SectionEditor: React.FC<SectionEditorProps> = ({
  section,
  onBack,
  onNext,
}) => {
  const methods = useFormContext<FormData>();

  // Map of section IDs to their titles
  const sectionTitles = {
    jobDetails: "Job Details",
    formDetails: "Form Details",
    screeningQuestions: "Screening Questions",
    videoResume: "Video Resume",
    proctoringSettings: "Proctoring Settings",
    referralRewards: "Referral & Rewards",
    quizSettings: "Quiz Settings",
  };

  const sectionOrder = [
    "jobDetails",
    "formDetails",
    "screeningQuestions",
    "videoResume",
    "proctoringSettings",
    "referralRewards",
    "quizSettings",
  ] as const;

  const currentIndex = sectionOrder.indexOf(
    section as (typeof sectionOrder)[number]
  );
  const nextSectionKey = sectionOrder[currentIndex + 1];
  const prevSectionKey = sectionOrder[currentIndex - 1];

  const isFirstSection = currentIndex === 0;
  const isLastSection = currentIndex === sectionOrder.length - 1;

  // Get the title for the current section
  const title =
    sectionTitles[section as keyof typeof sectionTitles] || "Unknown Section";

  // Render the appropriate editor based on the section
  const renderEditor = () => {
    switch (section) {
      case "jobDetails":
        return <JobDetailsEditor />;
      case "formDetails":
        return <FormDetailsEditor />;
      case "screeningQuestions":
        return <QualifyingQuestionsEditor />;
      case "videoResume":
        return <VideoResumeEditor />;
      case "proctoringSettings":
        return <ProctoringSettingsEditor />;
      case "referralRewards":
        return <ReferralRewardsEditor />;
      case "quizSettings":
        return <QuizSettingsEditor />;
      default:
        return <div>Unknown section</div>;
    }
  };

  const NavigationButtons = () => (
    <div className="flex items-start justify-between w-full">
      {!isFirstSection && (
        <div className="flex flex-col items-start">
          <button
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            ← Prev
          </button>
          {prevSectionKey && (
            <span className="text-xs text-gray-500 mt-1">
              {sectionTitles[prevSectionKey]}
            </span>
          )}
        </div>
      )}

      {/* Empty div to maintain spacing when back button is hidden */}
      {isFirstSection && <div></div>}

      {onNext && !isLastSection && (
        <div className="flex flex-col items-end">
          <button
            onClick={onNext}
            className="px-4 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-black transition-colors ml-1"
          >
            Next →
          </button>
          {nextSectionKey && (
            <span className="text-xs text-gray-500 mt-1">
              {sectionTitles[nextSectionKey]}
            </span>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>

      <div className="py-2">{renderEditor()}</div>

      <div className="sticky bottom-0 bg-white p-4 shadow-md">
        <NavigationButtons />
      </div>
    </div>
  );
};

export default React.memo(SectionEditor);
